import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, MapPin, Calendar, Package, Send, MessageSquare, Loader2, Edit, Trash2, Eye, FileText, Mail, CheckCircle, Clock, XCircle, DollarSign, Plus, Truck } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { jobsAPI, inventoryAPI, estimatesAPI } from '../utils/api';
import api from '../utils/api';
import InventoryManager from '../components/InventoryManager';

// Comprehensive status options for estimates
const ESTIMATE_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  { value: 'sent', label: 'Sent to Customer', color: 'bg-blue-100 text-blue-700', icon: Mail },
  { value: 'viewed', label: 'Viewed by Customer', color: 'bg-purple-100 text-purple-700', icon: Eye },
  { value: 'waiting_reply', label: 'Waiting Customer Reply', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  { value: 'accepted', label: 'Accepted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'deposit_pending', label: 'Accepted - Deposit Pending', color: 'bg-orange-100 text-orange-700', icon: DollarSign },
  { value: 'deposit_paid', label: 'Accepted - Deposit Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-teal-100 text-teal-700', icon: Calendar },
  { value: 'rejected', label: 'Rejected by Customer', color: 'bg-red-100 text-red-700', icon: XCircle },
  { value: 'expired', label: 'Expired', color: 'bg-gray-200 text-gray-600', icon: Clock },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-600', icon: XCircle },
  { value: 'cancelled_refunded', label: 'Cancelled - Deposit Refunded', color: 'bg-red-50 text-red-600', icon: DollarSign },
  { value: 'cancelled_retained', label: 'Cancelled - Deposit Retained', color: 'bg-red-100 text-red-700', icon: DollarSign },
];

// Job status options
const JOB_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'deposit_pending', label: 'Deposit Pending' },
  { value: 'deposit_paid', label: 'Deposit Paid' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'cancelled_refunded', label: 'Cancelled - Refunded' },
];

const JobDetails = ({ user }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [job, setJob] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'details');
  const [generatingEstimate, setGeneratingEstimate] = useState(false);
  
  // Job editing state
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Estimate viewing/editing state
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  const [showEditEstimateDialog, setShowEditEstimateDialog] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [updatingEstimateStatus, setUpdatingEstimateStatus] = useState(false);
  const [sendingEstimate, setSendingEstimate] = useState(false);
  
  // Estimate settings (available charges/materials)
  const [estimateSettings, setEstimateSettings] = useState(null);
  const [selectedCharges, setSelectedCharges] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [customCharge, setCustomCharge] = useState({ name: '', amount: 0 });
  
  // Chat state
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Determine if we're in control panel context
  const isControlPanel = window.location.pathname.includes('/control-panel');

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  // Listen for tab switch events from child components
  useEffect(() => {
    const handleTabSwitch = (event) => {
      if (event.detail) {
        setActiveTab(event.detail);
      }
    };
    
    window.addEventListener('switchTab', handleTabSwitch);
    return () => window.removeEventListener('switchTab', handleTabSwitch);
  }, []);

  const fetchJobDetails = async () => {
    try {
      const jobRes = await jobsAPI.get(jobId);
      setJob(jobRes.data);
      
      // Fetch inventory from enhanced endpoint
      try {
        const inventoryRes = await inventoryAPI.getByJob(jobId);
        // Enhanced endpoint returns {items: [...], summary: {...}}
        const inventoryData = inventoryRes.data?.items || inventoryRes.data || [];
        setInventory(Array.isArray(inventoryData) ? inventoryData : []);
      } catch (invError) {
        console.warn('Failed to load inventory:', invError);
        setInventory([]);
      }
      
      // Fetch estimates for this job
      try {
        const estRes = await api.get('/company/estimates', { params: { job_id: jobId } });
        setEstimates(estRes.data || []);
      } catch (estError) {
        console.warn('Failed to load estimates:', estError);
        setEstimates([]);
      }
      
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  // Chat functions
  const loadConversation = async () => {
    setChatLoading(true);
    try {
      // Try to find existing conversation for this job
      const convRes = await api.get('/conversations', { params: { job_id: jobId } });
      const conversations = convRes.data?.conversations || [];
      
      if (conversations.length > 0) {
        setConversation(conversations[0]);
        // Load messages
        const msgRes = await api.get(`/conversations/${conversations[0].id}/messages`);
        setMessages(msgRes.data?.messages || []);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setChatLoading(false);
    }
  };

  const startConversation = async () => {
    setChatLoading(true);
    try {
      const res = await api.post('/conversations', {
        job_id: jobId,
        customer_id: job.customer_id,
        initial_message: `Chat started for job ${job.job_number}`
      });
      setConversation({ id: res.data.id });
      loadConversation();
    } catch (error) {
      toast.error('Failed to start conversation');
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation?.id) return;
    
    setSending(true);
    try {
      await api.post(`/conversations/${conversation.id}/messages`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      // Reload messages
      const msgRes = await api.get(`/conversations/${conversation.id}/messages`);
      setMessages(msgRes.data?.messages || []);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Function to generate estimate from inventory
  const generateEstimate = async () => {
    setGeneratingEstimate(true);
    try {
      // Create and save estimate using the new endpoint
      const response = await api.post(`/company/estimates`, { job_id: jobId });
      if (response.data) {
        toast.success('Estimate created successfully!');
        // Add to estimates list
        setEstimates(prev => [response.data, ...prev]);
      }
    } catch (error) {
      console.error('Failed to create estimate:', error);
      toast.error(error.response?.data?.detail || 'Failed to create estimate. Make sure inventory items are added first.');
    } finally {
      setGeneratingEstimate(false);
    }
  };

  // Fetch estimates for this job
  const fetchEstimates = async () => {
    try {
      const response = await api.get(`/company/estimates`, { params: { job_id: jobId } });
      setEstimates(response.data || []);
    } catch (error) {
      console.warn('Failed to fetch estimates:', error);
    }
  };

  // Load estimates when switching to estimates tab
  useEffect(() => {
    if (activeTab === 'estimates' && jobId) {
      fetchEstimates();
    }
  }, [activeTab, jobId]);

  // Handle switching to estimates tab with auto-generate option
  const handleSwitchToEstimates = async (autoGenerate = false) => {
    setActiveTab('estimates');
    if (autoGenerate && inventory.length > 0) {
      // Small delay to let tab switch animation complete
      setTimeout(() => generateEstimate(), 300);
    }
  };

  // Update job status
  const updateJobStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await api.put(`/jobs/${jobId}`, { status: newStatus });
      setJob({ ...job, status: newStatus });
      const statusLabel = JOB_STATUSES.find(s => s.value === newStatus)?.label || newStatus;
      toast.success(`Job status updated to ${statusLabel}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update job status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Delete job
  const deleteJob = async () => {
    setDeleting(true);
    try {
      await api.delete(`/jobs/${jobId}`);
      toast.success('Job deleted successfully');
      navigate(isControlPanel ? '/control-panel/jobs' : '/dashboard');
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error('Failed to delete job');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // View estimate details
  const viewEstimate = (estimate) => {
    setSelectedEstimate(estimate);
    setShowEstimateDialog(true);
  };

  // Edit estimate
  const openEditEstimate = async (estimate) => {
    // Fetch estimate settings for available charges/materials
    try {
      const settingsRes = await api.get('/company/estimate-settings');
      setEstimateSettings(settingsRes.data);
    } catch (error) {
      console.warn('Failed to fetch estimate settings:', error);
    }
    
    // Set existing charges/materials on the estimate
    setSelectedCharges(estimate.additional_charges || []);
    setSelectedMaterials(estimate.packing_materials || []);
    setCustomCharge({ name: '', amount: 0 });
    
    setEditingEstimate({
      ...estimate,
      notes: estimate.notes || '',
      validity_days: estimate.validity_days || 30,
    });
    setShowEditEstimateDialog(true);
  };
  
  // Toggle additional charge selection
  const toggleCharge = (charge) => {
    const exists = selectedCharges.find(c => c.id === charge.id);
    if (exists) {
      setSelectedCharges(selectedCharges.filter(c => c.id !== charge.id));
    } else {
      setSelectedCharges([...selectedCharges, { ...charge, quantity: 1 }]);
    }
  };
  
  // Toggle packing material selection  
  const toggleMaterial = (material) => {
    const exists = selectedMaterials.find(m => m.id === material.id);
    if (exists) {
      setSelectedMaterials(selectedMaterials.filter(m => m.id !== material.id));
    } else {
      setSelectedMaterials([...selectedMaterials, { ...material, quantity: 1 }]);
    }
  };
  
  // Update charge quantity
  const updateChargeQuantity = (chargeId, quantity) => {
    setSelectedCharges(selectedCharges.map(c => 
      c.id === chargeId ? { ...c, quantity: Math.max(1, quantity) } : c
    ));
  };
  
  // Update material quantity
  const updateMaterialQuantity = (materialId, quantity) => {
    setSelectedMaterials(selectedMaterials.map(m => 
      m.id === materialId ? { ...m, quantity: Math.max(1, quantity) } : m
    ));
  };
  
  // Add custom charge
  const addCustomCharge = () => {
    if (customCharge.name && customCharge.amount > 0) {
      const newCharge = {
        id: `custom-${Date.now()}`,
        name: customCharge.name,
        amount: parseFloat(customCharge.amount),
        charge_type: 'fixed',
        quantity: 1,
        is_custom: true
      };
      setSelectedCharges([...selectedCharges, newCharge]);
      setCustomCharge({ name: '', amount: 0 });
    }
  };
  
  // Remove charge
  const removeCharge = (chargeId) => {
    setSelectedCharges(selectedCharges.filter(c => c.id !== chargeId));
  };
  
  // Remove material
  const removeMaterial = (materialId) => {
    setSelectedMaterials(selectedMaterials.filter(m => m.id !== materialId));
  };
  
  // Recalculate estimate totals
  const recalculateEstimateTotals = (pricing, charges, materials) => {
    const baseSubtotal = (pricing?.volume_cost || 0) + (pricing?.labour_cost || 0) + (pricing?.distance_cost || 0) + (pricing?.storage_cost || 0);
    
    // Calculate charges total
    const chargesTotal = charges.reduce((sum, c) => sum + ((c.amount || 0) * (c.quantity || 1)), 0);
    
    // Calculate materials total
    const materialsTotal = materials.reduce((sum, m) => sum + ((m.price || 0) * (m.quantity || 1)), 0);
    
    const subtotal = baseSubtotal + chargesTotal + materialsTotal;
    const vatRate = pricing?.vat_rate || 20;
    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;
    const depositPercentage = pricing?.deposit_percentage || 20;
    const depositAmount = total * (depositPercentage / 100);
    
    return {
      ...pricing,
      subtotal,
      additional_charges: chargesTotal,
      packing_materials: materialsTotal,
      vat_amount: vatAmount,
      total,
      deposit_amount: depositAmount
    };
  };

  // Update estimate status
  const updateEstimateStatus = async (estimateId, newStatus) => {
    setUpdatingEstimateStatus(true);
    try {
      await api.put(`/company/estimates/${estimateId}`, { status: newStatus });
      // Update local state
      setEstimates(estimates.map(e => 
        e.id === estimateId ? { ...e, status: newStatus } : e
      ));
      if (selectedEstimate?.id === estimateId) {
        setSelectedEstimate({ ...selectedEstimate, status: newStatus });
      }
      const statusLabel = ESTIMATE_STATUSES.find(s => s.value === newStatus)?.label || newStatus;
      toast.success(`Estimate status updated to ${statusLabel}`);
    } catch (error) {
      console.error('Failed to update estimate status:', error);
      toast.error('Failed to update estimate status');
    } finally {
      setUpdatingEstimateStatus(false);
    }
  };

  // Save estimate edits
  const saveEstimateEdits = async () => {
    if (!editingEstimate) return;
    
    // Recalculate totals with selected charges and materials
    const updatedPricing = recalculateEstimateTotals(editingEstimate.pricing, selectedCharges, selectedMaterials);
    
    try {
      await api.put(`/company/estimates/${editingEstimate.id}`, {
        notes: editingEstimate.notes,
        validity_days: editingEstimate.validity_days,
        pricing: updatedPricing,
        additional_charges: selectedCharges,
        packing_materials: selectedMaterials,
      });
      // Refresh estimates
      await fetchEstimates();
      setShowEditEstimateDialog(false);
      toast.success('Estimate updated successfully');
    } catch (error) {
      console.error('Failed to save estimate:', error);
      toast.error('Failed to save estimate');
    }
  };

  // Send estimate to customer
  const sendEstimateToCustomer = async (estimate) => {
    setSendingEstimate(true);
    try {
      await api.post(`/company/estimates/${estimate.id}/send`);
      // Update status to sent
      setEstimates(estimates.map(e => 
        e.id === estimate.id ? { ...e, status: 'sent' } : e
      ));
      if (selectedEstimate?.id === estimate.id) {
        setSelectedEstimate({ ...selectedEstimate, status: 'sent' });
      }
      toast.success('Estimate sent to customer!');
    } catch (error) {
      console.error('Failed to send estimate:', error);
      toast.error(error.response?.data?.detail || 'Failed to send estimate');
    } finally {
      setSendingEstimate(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Job not found</p>
      </div>
    );
  }

  // Helper to get status styling
  const getEstimateStatusConfig = (status) => {
    return ESTIMATE_STATUSES.find(s => s.value === status) || ESTIMATE_STATUSES[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-8" data-testid="job-details-page">
      <div className="max-w-7xl mx-auto px-4">
        <Button
          variant="outline"
          onClick={() => navigate(isControlPanel ? '/control-panel/jobs' : '/dashboard')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} className="mr-2" />
          {isControlPanel ? 'Back to Jobs' : 'Back to Dashboard'}
        </Button>

        {/* Job Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-3xl gradient-text mb-2">{job.job_number}</CardTitle>
                <div className="flex items-center gap-3">
                  {/* Status Dropdown */}
                  <Select 
                    value={job.status} 
                    onValueChange={updateJobStatus}
                    disabled={updatingStatus}
                  >
                    <SelectTrigger className="w-[200px]" data-testid="status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {JOB_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-500">
                    {job.customer_type}
                  </span>
                </div>
              </div>
              {/* Delete Button */}
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                data-testid="delete-job-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Delete Job
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="text-primary mt-1" size={20} />
                  <div>
                    <p className="font-semibold">From</p>
                    <p className="text-sm text-gray-600">
                      {job.from_address?.street || job.from_address?.line1 || 'Not specified'}<br />
                      {job.from_address?.city}, {job.from_address?.postal_code}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="text-secondary mt-1" size={20} />
                  <div>
                    <p className="font-semibold">To</p>
                    <p className="text-sm text-gray-600">
                      {job.to_address?.street || job.to_address?.line1 || 'Not specified'}<br />
                      {job.to_address?.city}, {job.to_address?.postal_code}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {job.move_date && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                <Calendar className="text-gray-500" size={18} />
                <span className="text-sm">Move Date: {new Date(job.move_date).toLocaleDateString()}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => {
          setActiveTab(value);
          if (value === 'chat' && !conversation) {
            loadConversation();
          }
        }} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details" data-testid="details-tab">Details</TabsTrigger>
            <TabsTrigger value="inventory" data-testid="inventory-tab">Inventory</TabsTrigger>
            <TabsTrigger value="estimates" data-testid="estimates-tab">Estimates</TabsTrigger>
            <TabsTrigger value="chat" data-testid="chat-tab">Chat</TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>Job Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">From</p>
                    <p className="font-medium">{job.from_address?.line1 || job.from_address?.city || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">To</p>
                    <p className="font-medium">{job.to_address?.line1 || job.to_address?.city || 'Not specified'}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Move Date</p>
                    <p className="font-medium">{job.move_date ? new Date(job.move_date).toLocaleDateString() : 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className="font-medium capitalize">{job.status?.replace('_', ' ')}</p>
                  </div>
                </div>
                {job.notes && (
                  <div>
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="font-medium">{job.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inventory">
            <InventoryManager 
              jobId={jobId} 
              mode="admin"
              readOnly={job.status === 'completed'}
              showVolume={true}
              onSwitchToEstimates={() => handleSwitchToEstimates(true)}
            />
          </TabsContent>

          <TabsContent value="estimates">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>Estimates & Quotes</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Job: {job.job_number} • {inventory.length} inventory items
                    </p>
                  </div>
                  <Button 
                    onClick={generateEstimate}
                    disabled={generatingEstimate || inventory.length === 0}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="generate-estimate-btn"
                  >
                    {generatingEstimate ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Package size={16} className="mr-2" />
                        Generate Estimate
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {estimates.length === 0 ? (
                  <div className="text-center py-12">
                    <Package size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 mb-2">No estimates yet for this job</p>
                    {inventory.length === 0 ? (
                      <p className="text-sm text-gray-500">Add inventory items first, then generate an estimate</p>
                    ) : (
                      <p className="text-sm text-gray-500">Click &quot;Generate Estimate&quot; to create a quote from {inventory.length} inventory items</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {estimates.map((estimate) => {
                      const statusConfig = getEstimateStatusConfig(estimate.status);
                      return (
                        <div key={estimate.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                          <div className="flex flex-col lg:flex-row justify-between gap-4">
                            {/* Left side - Info */}
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-semibold text-lg">{estimate.estimate_number}</h4>
                                {/* Status Dropdown */}
                                <Select 
                                  value={estimate.status} 
                                  onValueChange={(newStatus) => updateEstimateStatus(estimate.id, newStatus)}
                                  disabled={updatingEstimateStatus}
                                >
                                  <SelectTrigger className={`w-[220px] h-8 text-xs ${statusConfig.color}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ESTIMATE_STATUSES.map((status) => (
                                      <SelectItem key={status.value} value={status.value}>
                                        <span className="flex items-center gap-2">
                                          <status.icon size={14} />
                                          {status.label}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <p><span className="font-medium">{estimate.total_items}</span> items • <span className="font-medium">{estimate.total_volume?.toFixed(2) || '0.00'}</span> m³ total volume</p>
                                <p className="text-xs text-gray-400">
                                  Created: {new Date(estimate.created_at).toLocaleDateString()} 
                                  {estimate.valid_until && ` • Valid until: ${new Date(estimate.valid_until).toLocaleDateString()}`}
                                </p>
                              </div>
                            </div>

                            {/* Right side - Pricing & Actions */}
                            <div className="flex flex-col items-end">
                              <p className="text-2xl font-bold text-primary">
                                {estimate.pricing?.currency || '£'}{estimate.pricing?.total?.toFixed(2) || '0.00'}
                              </p>
                              <p className="text-xs text-gray-500">Total (inc. VAT)</p>
                              <p className="text-sm text-gray-600">
                                Deposit: {estimate.pricing?.currency || '£'}{estimate.pricing?.deposit_amount?.toFixed(2) || '0.00'}
                              </p>
                              
                              {/* Action Buttons */}
                              <div className="flex flex-wrap gap-2 mt-3 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => viewEstimate(estimate)}
                                  data-testid={`view-estimate-${estimate.id}`}
                                >
                                  <Eye size={14} className="mr-1" />
                                  View
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditEstimate(estimate)}
                                  data-testid={`edit-estimate-${estimate.id}`}
                                >
                                  <Edit size={14} className="mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => sendEstimateToCustomer(estimate)}
                                  disabled={sendingEstimate || estimate.status === 'sent'}
                                  className="bg-blue-600 hover:bg-blue-700"
                                  data-testid={`send-estimate-${estimate.id}`}
                                >
                                  {sendingEstimate ? (
                                    <Loader2 size={14} className="mr-1 animate-spin" />
                                  ) : (
                                    <Mail size={14} className="mr-1" />
                                  )}
                                  {estimate.status === 'sent' ? 'Sent' : 'Send to Customer'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="chat">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare size={20} />
                  Chat with {isControlPanel ? 'Customer' : 'Company'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chatLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin mr-2" />
                    <span>Loading chat...</span>
                  </div>
                ) : !conversation ? (
                  <div className="text-center py-12">
                    <MessageSquare size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600 mb-4">No conversation started for this job</p>
                    <Button onClick={startConversation} data-testid="start-chat-btn">
                      <MessageSquare size={16} className="mr-2" />
                      Start Conversation
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col h-[400px]">
                    {/* Messages */}
                    <ScrollArea className="flex-1 pr-4">
                      <div className="space-y-4">
                        {messages.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">No messages yet. Start the conversation!</p>
                        ) : (
                          messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[70%] ${msg.sender_id === user?.id ? 'order-2' : ''}`}>
                                <div
                                  className={`rounded-lg p-3 ${
                                    msg.sender_id === user?.id
                                      ? 'bg-primary text-white'
                                      : 'bg-gray-100'
                                  }`}
                                >
                                  <p className="text-sm">{msg.content}</p>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  {formatTime(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>
                    
                    {/* Message Input */}
                    <form onSubmit={sendMessage} className="mt-4 pt-4 border-t flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1"
                        disabled={sending}
                        data-testid="chat-input"
                      />
                      <Button type="submit" disabled={!newMessage.trim() || sending} data-testid="send-chat-btn">
                        {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                      </Button>
                    </form>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Job</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete job {job.job_number}? This action cannot be undone.
              All associated inventory items and estimates will also be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteJob} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Delete Job
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Estimate View Dialog */}
      <Dialog open={showEstimateDialog} onOpenChange={setShowEstimateDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} />
              Estimate Details - {selectedEstimate?.estimate_number}
            </DialogTitle>
            <DialogDescription>
              View and manage estimate details, pricing, and status
            </DialogDescription>
          </DialogHeader>
          {selectedEstimate && (
            <div className="space-y-4">
              {/* Estimate Status & Actions */}
              <div className="flex flex-wrap justify-between items-center gap-4 pb-4 border-b">
                <div className="flex items-center gap-3">
                  <Select 
                    value={selectedEstimate.status} 
                    onValueChange={(newStatus) => updateEstimateStatus(selectedEstimate.id, newStatus)}
                    disabled={updatingEstimateStatus}
                  >
                    <SelectTrigger className={`w-[220px] ${getEstimateStatusConfig(selectedEstimate.status).color}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTIMATE_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          <span className="flex items-center gap-2">
                            <status.icon size={14} />
                            {status.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-gray-500">
                  Created: {new Date(selectedEstimate.created_at).toLocaleDateString()}
                  {selectedEstimate.valid_until && (
                    <span className="ml-2">• Valid until: {new Date(selectedEstimate.valid_until).toLocaleDateString()}</span>
                  )}
                </div>
              </div>

              {/* Items Summary with Dimensions */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Items ({selectedEstimate.total_items}) - Total Volume: {selectedEstimate.total_volume?.toFixed(2) || '0.00'} m³</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="py-2 pr-4">Item</th>
                        <th className="py-2 px-2 text-center">Qty</th>
                        <th className="py-2 px-2 text-center">Category</th>
                        <th className="py-2 px-2 text-right">Volume (m³)</th>
                        <th className="py-2 pl-2 text-center">Fragile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEstimate.items?.map((item, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0">
                          <td className="py-2 pr-4 font-medium">{item.name}</td>
                          <td className="py-2 px-2 text-center">{item.quantity}</td>
                          <td className="py-2 px-2 text-center text-gray-500">{item.category || '-'}</td>
                          <td className="py-2 px-2 text-right">{(item.volume * item.quantity)?.toFixed(3) || '0.000'}</td>
                          <td className="py-2 pl-2 text-center">
                            {item.is_fragile ? <span className="text-orange-500">⚠️</span> : '-'}
                          </td>
                        </tr>
                      )) || (
                        <tr>
                          <td colSpan={5} className="py-4 text-center text-gray-500">No items breakdown available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Pricing Breakdown</h4>
                <div className="space-y-2">
                  {selectedEstimate.pricing?.volume_cost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Volume Cost ({selectedEstimate.total_volume?.toFixed(2)} m³ × £{selectedEstimate.pricing?.volume_rate || 50}/m³)</span>
                      <span>{selectedEstimate.pricing?.currency || '£'}{selectedEstimate.pricing?.volume_cost?.toFixed(2)}</span>
                    </div>
                  )}
                  {selectedEstimate.pricing?.labour_cost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Labour Cost</span>
                      <span>{selectedEstimate.pricing?.currency || '£'}{selectedEstimate.pricing?.labour_cost?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span>Subtotal</span>
                    <span>{selectedEstimate.pricing?.currency || '£'}{selectedEstimate.pricing?.subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT ({selectedEstimate.pricing?.vat_rate || 20}%)</span>
                    <span>{selectedEstimate.pricing?.currency || '£'}{selectedEstimate.pricing?.vat_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span>Total</span>
                    <span className="text-primary">{selectedEstimate.pricing?.currency || '£'}{selectedEstimate.pricing?.total?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Deposit Required ({selectedEstimate.pricing?.deposit_percentage || 20}%)</span>
                    <span>{selectedEstimate.pricing?.currency || '£'}{selectedEstimate.pricing?.deposit_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedEstimate.notes && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <p className="text-sm text-gray-700">{selectedEstimate.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowEstimateDialog(false)}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowEstimateDialog(false);
                openEditEstimate(selectedEstimate);
              }}
            >
              <Edit size={16} className="mr-2" />
              Edit
            </Button>
            <Button 
              onClick={() => sendEstimateToCustomer(selectedEstimate)}
              disabled={sendingEstimate || selectedEstimate?.status === 'sent'}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEstimate ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Mail size={16} className="mr-2" />
              )}
              {selectedEstimate?.status === 'sent' ? 'Already Sent' : 'Send to Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Estimate Dialog */}
      <Dialog open={showEditEstimateDialog} onOpenChange={setShowEditEstimateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={20} />
              Edit Estimate - {editingEstimate?.estimate_number}
            </DialogTitle>
            <DialogDescription>
              Update estimate status, pricing, and details
            </DialogDescription>
          </DialogHeader>
          {editingEstimate && (
            <div className="space-y-4">
              {/* Status */}
              <div>
                <Label>Status</Label>
                <Select 
                  value={editingEstimate.status} 
                  onValueChange={(newStatus) => setEditingEstimate({...editingEstimate, status: newStatus})}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTIMATE_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        <span className="flex items-center gap-2">
                          <status.icon size={14} />
                          {status.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Pricing Adjustments */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Subtotal (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingEstimate.pricing?.subtotal || 0}
                    onChange={(e) => {
                      const subtotal = parseFloat(e.target.value) || 0;
                      const vatRate = editingEstimate.pricing?.vat_rate || 20;
                      const vatAmount = subtotal * (vatRate / 100);
                      const total = subtotal + vatAmount;
                      const depositPercentage = editingEstimate.pricing?.deposit_percentage || 20;
                      const depositAmount = total * (depositPercentage / 100);
                      setEditingEstimate({
                        ...editingEstimate,
                        pricing: {
                          ...editingEstimate.pricing,
                          subtotal,
                          vat_amount: vatAmount,
                          total,
                          deposit_amount: depositAmount
                        }
                      });
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>VAT Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingEstimate.pricing?.vat_rate || 20}
                    onChange={(e) => {
                      const vatRate = parseFloat(e.target.value) || 0;
                      const subtotal = editingEstimate.pricing?.subtotal || 0;
                      const vatAmount = subtotal * (vatRate / 100);
                      const total = subtotal + vatAmount;
                      const depositPercentage = editingEstimate.pricing?.deposit_percentage || 20;
                      const depositAmount = total * (depositPercentage / 100);
                      setEditingEstimate({
                        ...editingEstimate,
                        pricing: {
                          ...editingEstimate.pricing,
                          vat_rate: vatRate,
                          vat_amount: vatAmount,
                          total,
                          deposit_amount: depositAmount
                        }
                      });
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Total (£) - Auto-calculated</Label>
                  <Input
                    type="number"
                    value={editingEstimate.pricing?.total?.toFixed(2) || 0}
                    disabled
                    className="mt-1 bg-gray-100"
                  />
                </div>
                <div>
                  <Label>Deposit Percentage (%)</Label>
                  <Input
                    type="number"
                    step="1"
                    value={editingEstimate.pricing?.deposit_percentage || 20}
                    onChange={(e) => {
                      const depositPercentage = parseFloat(e.target.value) || 0;
                      const total = editingEstimate.pricing?.total || 0;
                      const depositAmount = total * (depositPercentage / 100);
                      setEditingEstimate({
                        ...editingEstimate,
                        pricing: {
                          ...editingEstimate.pricing,
                          deposit_percentage: depositPercentage,
                          deposit_amount: depositAmount
                        }
                      });
                    }}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Deposit Amount Display */}
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="flex justify-between font-semibold">
                  <span>Deposit Amount</span>
                  <span>£{editingEstimate.pricing?.deposit_amount?.toFixed(2) || '0.00'}</span>
                </div>
              </div>

              <Separator />

              {/* Additional Charges Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Truck size={18} />
                    Additional Charges
                  </Label>
                </div>
                
                {/* Available Charges from Settings */}
                {estimateSettings?.additional_charges?.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Select from available charges:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {estimateSettings.additional_charges.filter(c => c.is_active).map((charge) => {
                        const isSelected = selectedCharges.some(sc => sc.id === charge.id);
                        return (
                          <div 
                            key={charge.id} 
                            className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition ${isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-100'}`}
                            onClick={() => toggleCharge(charge)}
                          >
                            <Checkbox checked={isSelected} />
                            <div className="flex-1 text-sm">
                              <p className="font-medium">{charge.name}</p>
                              <p className="text-gray-500">£{charge.amount}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Selected Charges */}
                {selectedCharges.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Selected charges:</p>
                    {selectedCharges.map((charge) => (
                      <div key={charge.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{charge.name}</p>
                          {charge.is_custom && <Badge variant="outline" className="text-xs">Custom</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={charge.quantity || 1}
                            onChange={(e) => updateChargeQuantity(charge.id, parseInt(e.target.value) || 1)}
                            className="w-16 h-8 text-sm"
                          />
                          <span className="font-medium text-sm">× £{charge.amount}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeCharge(charge.id)}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="text-right text-sm font-medium">
                      Charges Total: £{selectedCharges.reduce((sum, c) => sum + ((c.amount || 0) * (c.quantity || 1)), 0).toFixed(2)}
                    </div>
                  </div>
                )}
                
                {/* Add Custom Charge */}
                <div className="flex items-end gap-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <Label className="text-xs">Custom Charge Name</Label>
                    <Input
                      placeholder="e.g., Piano moving"
                      value={customCharge.name}
                      onChange={(e) => setCustomCharge({...customCharge, name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Amount (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={customCharge.amount || ''}
                      onChange={(e) => setCustomCharge({...customCharge, amount: parseFloat(e.target.value) || 0})}
                      className="mt-1"
                    />
                  </div>
                  <Button 
                    type="button" 
                    size="sm" 
                    onClick={addCustomCharge}
                    disabled={!customCharge.name || !customCharge.amount}
                  >
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Packing Materials Section */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Package size={18} />
                  Packing Materials
                </Label>
                
                {/* Available Materials from Settings */}
                {estimateSettings?.packing_materials?.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Select from available packing packs:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {estimateSettings.packing_materials.filter(m => m.is_active).map((material) => {
                        const isSelected = selectedMaterials.some(sm => sm.id === material.id);
                        return (
                          <div 
                            key={material.id} 
                            className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition ${isSelected ? 'bg-green-50 border-green-300' : 'bg-white hover:bg-gray-100'}`}
                            onClick={() => toggleMaterial(material)}
                          >
                            <Checkbox checked={isSelected} />
                            <div className="flex-1 text-sm">
                              <p className="font-medium">{material.name}</p>
                              <p className="text-gray-500">£{material.price}</p>
                              {material.includes?.length > 0 && (
                                <p className="text-xs text-gray-400 truncate">{material.includes.join(', ')}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Selected Materials */}
                {selectedMaterials.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Selected materials:</p>
                    {selectedMaterials.map((material) => (
                      <div key={material.id} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{material.name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={material.quantity || 1}
                            onChange={(e) => updateMaterialQuantity(material.id, parseInt(e.target.value) || 1)}
                            className="w-16 h-8 text-sm"
                          />
                          <span className="font-medium text-sm">× £{material.price}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeMaterial(material.id)}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="text-right text-sm font-medium">
                      Materials Total: £{selectedMaterials.reduce((sum, m) => sum + ((m.price || 0) * (m.quantity || 1)), 0).toFixed(2)}
                    </div>
                  </div>
                )}
                
                {estimateSettings?.packing_materials?.length === 0 && selectedMaterials.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No packing materials configured. Go to Estimate Settings to add packing packs.
                  </p>
                )}
              </div>

              <Separator />

              {/* Validity Days */}
              <div>
                <Label>Validity (Days)</Label>
                <Input
                  type="number"
                  value={editingEstimate.validity_days || 30}
                  onChange={(e) => setEditingEstimate({...editingEstimate, validity_days: parseInt(e.target.value) || 30})}
                  className="mt-1"
                />
              </div>

              {/* Notes */}
              <div>
                <Label>Notes (shown on estimate)</Label>
                <Textarea
                  value={editingEstimate.notes || ''}
                  onChange={(e) => setEditingEstimate({...editingEstimate, notes: e.target.value})}
                  placeholder="Add any notes or special conditions..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEstimateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveEstimateEdits}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetails;
